import { ChangeDetectionStrategy, Component, computed, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MotifIndisponibilite } from '../../../shared/models/agenda/indisponibilite.model';
import { AgendaService } from '../../../shared/providers/agenda/agenda.service';
import { AlertController, IonicModule, ModalController, ToastController } from '@ionic/angular';
import { Events } from '../../../shared/providers/events/events.provider';
import { FormsModule } from '@angular/forms';
import { NgIf } from '@angular/common';
import moment from 'moment';
import { DateTimePickerComponent } from '../../../shared/design-system/inputs/date-time-picker/date-time-picker.component';

@Component({
  selector: 'agenda-saisie-indispo',
  templateUrl: 'agenda-saisie-indispo.modal.html',
  standalone: true,
  styleUrls: ['agenda-saisie-indispo.modal.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IonicModule, FormsModule, NgIf, DateTimePickerComponent],
})
export class AgendaSaisieIndispoModal {
  /**
   * Motif de l'indisponibilité
   */
  motif: string;

  /**
   * Passage de l'enum
   */
  motifIndisponibilite = MotifIndisponibilite;

  dateDebut = signal<Date>(null);

  dateFin = signal<Date>(null);

  dureeTropCourte = computed(() => {
    return moment(this.dateFin()).diff(moment(this.dateDebut()), 'minutes') < 30;
  });

  commentaireValid = signal<boolean>(true);

  commentaire: string = '';

  isLoading = signal<boolean>(false);

  isCommentaireFalcultatif = signal<boolean>(true);

  isIconUp: boolean = true;

  selectedOption: MotifIndisponibilite;

  accidentTypeOptions = [
    { value: this.motifIndisponibilite.Vehicule, label: 'Problème véhicule' },
    { value: this.motifIndisponibilite.Maladie, label: 'Maladie' },
    { value: this.motifIndisponibilite.Deplacement, label: "Problème de déplacement, d'accès" },
    { value: this.motifIndisponibilite.Accident, label: 'Accident de trajet, de travail' },
    { value: this.motifIndisponibilite.Repos, label: "Repos suite à sortie d'astreinte" },
    { value: this.motifIndisponibilite.Autre, label: 'Autres' },
  ];

  constructor(
    private agendaService: AgendaService,
    private alertCtrl: AlertController,
    private translateService: TranslateService,
    private events: Events,
    private toastCtrl: ToastController,
    private modalController: ModalController
  ) {
    const now = new Date();
    now.setSeconds(0);
    now.setMilliseconds(0);

    this.dateDebut.set(now);
    this.dateFin.set(now);
  }

  changeMotif($event) {
    this.isIconUp = !this.isIconUp;
    this.checkForm();
  }

  changeCommentaire($event) {
    this.commentaire = $event;
    this.checkForm();
  }

  changeDateDebut($event) {
    this.dateDebut.set($event);
  }

  changeDateFin($event) {
    this.dateFin.set($event);
  }

  checkForm() {
    let isValid = this.selectedOption !== undefined;
    if (this.selectedOption === MotifIndisponibilite.Autre && this.commentaire.length <= 0) {
      isValid = false;
    }
    this.commentaireValid.set(isValid);
    this.isCommentaireFalcultatif.set(this.selectedOption !== MotifIndisponibilite.Autre);
  }

  validForm() {
    if (this.commentaireValid() && !this.dureeTropCourte()) {
      this.isLoading.set(true);
      this.agendaService
        .addIndispoImmediate(this.dateDebut(), this.dateFin(), this.selectedOption, this.commentaire)
        .subscribe({
          next: async (res) => {
            if (res) {
              this.events.publish('agenda:reload');
              await this.afficherToast();
            } else {
              await this.afficherAlerte();
            }
          },
          error: async () => {
            await this.afficherAlerte();
          },
        });
    }
  }

  async afficherToast() {
    await this.modalController.dismiss();
    let toast: HTMLIonToastElement = await this.toastCtrl.create({
      message: this.translateService.instant("L'indisponibilité immédiate a bien été créée."),
      duration: 3000,
      position: 'bottom',
    });
    await toast.present();
  }

  async afficherAlerte() {
    this.isLoading.set(false);
    let alert: HTMLIonAlertElement = await this.alertCtrl.create({
      header: this.translateService.instant("Erreur d'enregistrement de l'indisponibilité"),
      subHeader: this.translateService.instant('Veuillez réessayer ultérieurement'),
      buttons: ['OK'],
    });
    await alert.present();
  }

  async close(): Promise<void> {
    await this.modalController.dismiss();
  }

  selectOption(option) {
    this.selectedOption = option.value;
    this.motif = option.label;
    this.checkForm();
  }

  handleDismiss() {
    this.isIconUp = !this.isIconUp;
  }

  protected readonly moment = moment;
}
